import React, { useState } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { doc, getDoc, collection, query, where, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import IPService from '../../services/IPService';
import './Auth.css';

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);


  // Check if user email is approved (activated from waitlist)
  const checkUserApproved = async (email, uid) => {
    try {
      // If uid provided, check direct user document first
      if (uid) {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          return { approved: true, reason: 'existing_user' };
        }
      }

      // First check if user exists in users collection (already activated)
      const usersQuery = query(collection(db, 'users'), where('email', '==', email.toLowerCase()));
      const userSnapshot = await getDocs(usersQuery);
      
      if (!userSnapshot.empty) {
        return { approved: true, reason: 'existing_user' };
      }

      // Check if user is in waitlist with activated status
      const waitlistQuery = query(
        collection(db, 'waitlist'), 
        where('email', '==', email.toLowerCase()),
        where('status', '==', 'activated')
      );
      const waitlistSnapshot = await getDocs(waitlistQuery);
      
      if (!waitlistSnapshot.empty) {
        return { approved: true, reason: 'waitlist_approved' };
      }

      return { approved: false, reason: 'not_approved' };
    } catch (error) {
      console.error('Error checking user approval:', error);
      return { approved: false, reason: 'error' };
    }
  };

  // Create user document for approved users on first sign-in
  const createUserDocument = async (user, approval) => {
    try {
      // Check if user document already exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // Check if there's a pre-approved user document for this email
        const usersQuery = query(collection(db, 'users'), where('email', '==', user.email.toLowerCase()));
        const userSnapshot = await getDocs(usersQuery);
        
        if (!userSnapshot.empty) {
          // Pre-approved user document exists, copy it to the new UID
          const preApprovedDoc = userSnapshot.docs[0];
          const preApprovedData = preApprovedDoc.data();
          
          // Create user document with the new UID but keep existing data
          await setDoc(doc(db, 'users', user.uid), {
            ...preApprovedData,
            email: user.email, // Ensure email matches
            updatedAt: new Date(),
            firebaseAuthUID: user.uid // Track the actual Firebase Auth UID
          }, { merge: true });
          
          // Delete the old pre-approved document
          await deleteDoc(doc(db, 'users', preApprovedDoc.id));
          
          console.log('Pre-approved user document migrated for:', user.email);
        } else {
          // Create new user document with free plan
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            isSubscribed: true,
            subscriptionType: 'free',
            subscriptionStartDate: new Date(),
            maxAccounts: 1,
            maxVideosPerMonth: 10,
            videosUsedThisMonth: 0,
            currentPeriodStart: new Date(),
            twitterUsernames: [],
            activatedFromWaitlist: approval.reason === 'waitlist_approved',
            createdAt: new Date(),
            updatedAt: new Date(),
            isUnlimited: false,
            totalVideos: 0,
            recentImports: []
          }, { merge: true });
          
          console.log('New user document created for:', user.email);
        }
      }
    } catch (error) {
      console.error('Error creating user document:', error);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    
    if (!password) {
      setShowPassword(true);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Sign in existing user
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Check approval and create document if needed
      const signinApproval = await checkUserApproved(userCredential.user.email, userCredential.user.uid);
      if (signinApproval.approved) {
        await createUserDocument(userCredential.user, signinApproval);
      }
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email. Please create an account first.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Sign in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError('');

    try {
      const currentIP = await IPService.getCurrentIP();
      const isLimited = await IPService.checkIPLimited(currentIP);
      
      if (isLimited) {
        setError('Account creation is not allowed from this location. Please contact support.');
        setLoading(false);
        return;
      }

      const provider = new GoogleAuthProvider();
      
      // Configure provider to prevent automatic account creation
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      // First, try to sign in
      try {
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        
        // Check if this user is approved
        const approval = await checkUserApproved(user.email, user.uid);
        
        if (!approval.approved) {
          // User is not approved, sign them out immediately
          await auth.signOut();
          setError('This Google account is not approved for access. Please join our waitlist first or contact support.');
          setLoading(false);
          return;
        }

        // User is approved, create user document if needed
        await createUserDocument(user, approval);
        
        // User can proceed
        console.log('Approved user signed in:', user.email);
        
      } catch (authError) {
        if (authError.code === 'auth/popup-closed-by-user') {
          setError('Sign in was cancelled.');
        } else if (authError.code === 'auth/account-exists-with-different-credential') {
          setError('An account already exists with this email using a different sign-in method.');
        } else {
          setError('Google sign in failed. Please try again or use email/password.');
        }
      }
      
    } catch (error) {
      console.error('Google auth error:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="clerk-auth-container">
      <div className="clerk-auth-wrapper">
        <div className="clerk-auth-card">
          <div className="clerk-auth-header">
            <div className="clerk-logo">
              <img src="/logo.png" alt="Logo" className="clerk-logo-image" />
            </div>
            <div className="clerk-auth-title">
              <h1>Sign in to Twizz</h1>
              <p className="clerk-auth-description">
                Welcome back! Please sign in to continue
              </p>
            </div>
          </div>

          <div className="clerk-auth-content">
            <div className="clerk-social-buttons">
              <button 
                className="clerk-social-btn"
                onClick={handleGoogleAuth}
                disabled={loading}
                type="button"
              >
                {loading ? (
                  <div className="clerk-spinner-small"></div>
                ) : (
                  <>
                    <svg className="clerk-google-icon" width="16" height="16" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>
            </div>

            <div className="clerk-divider">
              <div className="clerk-divider-line"></div>
              <span className="clerk-divider-text">or</span>
              <div className="clerk-divider-line"></div>
            </div>

            <form onSubmit={handleEmailAuth} className="clerk-form">
              <div className="clerk-field">
                <label className="clerk-label" htmlFor="email">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="clerk-input"
                  disabled={loading}
                />
              </div>

              {showPassword && (
                <div className="clerk-field">
                  <label className="clerk-label" htmlFor="password">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="clerk-input"
                    disabled={loading}

                  />
                </div>
              )}

              {error && (
                <div className="clerk-error">
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                className="clerk-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <div className="clerk-spinner"></div>
                ) : (
                  showPassword ? 'Sign in' : 'Continue'
                )}
              </button>
            </form>

            <div className="clerk-footer">
              <button 
                className="clerk-link-btn"
                onClick={() => window.history.pushState(null, '', '/signup')}
                type="button"
                disabled={loading}
              >
                Don't have an account? Create one
              </button>
            </div>
          </div>

          <div className="clerk-secured">
            <span className="clerk-secured-text">Secured by</span>
            <div className="clerk-secured-logo">
              <span className="clerk-secured-icon">⚡</span>
              <span>Twizz</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen; 